import React from "react";
import { map } from "lodash";
import { List, Map } from "immutable";
import { FormattedMessage, useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition } from "griddle-react";
import { Badge } from "reactstrap";

import { Localisation } from "@localisation/index";
import { Locale } from "@localisation/models";
import { Table } from "@ui/Table";
import { CellDate } from "@ui/Table/Cells";
import { messages as uiMessages } from "@ui/messages";
import { Copyright } from "../Copyright";
import { Album, Track, Artist } from "../../models";
import { messages } from "../../messages";

interface TrackPreviewTableProps {
    album: Album;
    tracks: Track[];
    locale: Locale;
}

interface Row {
    entity: Track;
}

// Type the table component
type TypedTable = new () => Table<Row>;
const TypedTable = Table as TypedTable;

type Props = TrackPreviewTableProps;

const CellYesNo = ({ value }: { value: boolean }) => (
    <FormattedMessage {...(value ? uiMessages.buttonYes : uiMessages.buttonNo)} />
);

const CellCopyright = ({ value }: { value: Map<string, any> }) => {
    const track = new Track(value.toJS());
    return <Copyright entity={track} />;
};

const TrackPreviewTable = ({ album, tracks, locale }: Props) => {
    const intl = useIntl();
    const data = map(tracks, (track) => {
        return {entity: track} as Row;
    });

    // The artists cell
    const CellArtists = ({ value }: { value: List<Artist> }) => {
        const artists = value.map((artist) => new Artist(artist.toJS())).toArray();
        return (
            <span>
                {artists.map((artist, i) => (
                    <span key={i} className={artist.pivot.isMain ? "" : "text-muted"}>
                        {artist.name}{" "}
                        {artist.id == null &&
                            <Badge color="primary">
                                <FormattedMessage {...messages.newArtist} />
                            </Badge>
                        }
                        {" "}(<em>{Localisation.translateMap(artist.pivot.role.label, locale)}</em>)
                        <br />
                    </span>
                ))}
            </span>
        );
    };

    // The preorder date cell.
    const CellPreorderDate = ({ value }: { value: Date }) => {
        if (value ==  null) {
            value = album.preorderDate;
        }
        return <CellDate value={value} />;
    };

    // The release date cell.
    const CellReleaseDate = ({ value }: { value: Date }) => {
        if (value ==  null) {
            value = album.releaseDate;
        }
        return <CellDate value={value} />;
    };

    return (
        <TypedTable data={data} layout="basic" size="small">
            <RowDefinition>
                <ColumnDefinition
                    key="disc"
                    id="entity.pivot.disc"
                    title={intl.formatMessage(messages.albumTrackColumnDisc)} />
                <ColumnDefinition
                    key="track"
                    id="entity.pivot.track"
                    title={intl.formatMessage(messages.albumTrackColumnTrack)} />
                <ColumnDefinition
                    key="title"
                    id="entity.title"
                    title={intl.formatMessage(messages.trackColumnTitle)} />
                <ColumnDefinition
                    key="artists"
                    id="entity.artists"
                    title={intl.formatMessage(messages.trackColumnArtists)}
                    customComponent={CellArtists} />
                <ColumnDefinition
                    key="label"
                    id="entity.label.name"
                    title={intl.formatMessage(messages.trackColumnLabel)} />
                <ColumnDefinition
                    key="copyright"
                    id="entity"
                    title={intl.formatMessage(messages.trackColumnCopyright)}
                    customComponent={CellCopyright} />
                <ColumnDefinition
                    key="isrc"
                    id="entity.isrc"
                    title={intl.formatMessage(messages.trackColumnIsrc)} />
                <ColumnDefinition
                    key="preorderDate"
                    id="entity.pivot.preorderDate"
                    title={intl.formatMessage(messages.albumTrackColumnPreorderDate)}
                    customComponent={CellPreorderDate} />
                <ColumnDefinition
                    key="releaseDate"
                    id="entity.pivot.releaseDate"
                    title={intl.formatMessage(messages.albumTrackColumnReleaseDate)}
                    customComponent={CellReleaseDate} />
                <ColumnDefinition
                    key="explicit"
                    id="entity.explicit"
                    title={intl.formatMessage(messages.trackColumnExplicit)}
                    customComponent={CellYesNo} />
                <ColumnDefinition
                    key="gapless"
                    id="entity.gapless"
                    title={intl.formatMessage(messages.trackColumnGapless)}
                    customComponent={CellYesNo} />
                <ColumnDefinition
                    key="albumOnly"
                    id="entity.pivot.albumOnly"
                    title={intl.formatMessage(messages.albumTrackColumnAlbumOnly)}
                    customComponent={CellYesNo} />
            </RowDefinition>
        </TypedTable>
    );
};

export {
    TrackPreviewTable,
    Props as TrackPreviewTableProps,
    CellYesNo,
    CellCopyright,
};
